import { useAutoAnimate } from "@formkit/auto-animate/react";
import { faClock } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryAppointmentIdAsMember } from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { useClubSettings } from "apps/portal/src/providers";
import { RouteFeature } from "apps/portal/src/routes";
import useGymflowModels from "apps/portal/src/store";
import { DateTime, Duration } from "luxon";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { Spinner } from "../../atoms";
import { CalendarDateIcon } from "../../atoms/icons/CalendarDateIcon";
import { BookingStatusAppointmentBadge } from "../../Badges/BookingStatusAppointmentBadge";
import { FacilityAvatar } from "../../molecules/FacilityAvatar";
import { StaffAvatar } from "../../molecules/StaffAvatar";
import { HostedPagesBackHeader } from "../components/HostedPagesBackHeader";
import useRedirectIfNotAuthenticated from "../useRedirectIfNotAuthenticated";
import { HostedPagesProfileBookingAppointmentsOverviewActions } from "./components/HostedPagesProfileBookingAppointmentsOverviewActions";

export const HostedPagesProfileBookingAppointmentsOverview = () => {
  const { createSiteOrEmbedLink } = usePortalRoutes();

  useRedirectIfNotAuthenticated(
    createSiteOrEmbedLink(
      RouteFeature.SiteBuyMembershipType.replace(":membershipType", "all"),
    ),
  );

  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { t } = useTranslation();
  const { isEmbed } = usePortalRoutes();
  const [parent] = useAutoAnimate();
  const { api } = useGymflowModels();
  const { timezone } = useClubSettings();

  const { data: dataAppointment, isLoading: isLoadingAppointment } =
    useQueryAppointmentIdAsMember({
      api,
      id: Number(appointmentId),
    });

  const isShowActions = !(
    dataAppointment?.status === "LATE_CANCELLED" ||
    dataAppointment?.status === "CANCELLED" ||
    dataAppointment?.status === "NO_SHOW"
  );

  return (
    <div
      className={cn(
        "track-height flex w-full flex-col bg-gray-50 dark:bg-gray-900",
        {
          "h-fit": isEmbed,
          "h-full": !isEmbed,
        },
      )}
    >
      <HostedPagesBackHeader
        label={t("page.hostedPagesProfileBookingAppointmentsOverview.header")}
        link={createSiteOrEmbedLink(
          RouteFeature.SiteProfileBookingAppointments,
        )}
      />
      <div
        ref={parent}
        className={cn("flex flex-col items-center py-6 overflow-y-auto px-4", {
          "h-fit": isEmbed,
          "h-full": !isEmbed,
        })}
      >
        {isLoadingAppointment ? (
          <Spinner className="size-7" />
        ) : (
          <div className="flex w-full max-w-lg flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="flex flex-row items-center justify-between">
                <div className="dark:text-gray-25 text-lg font-bold text-gray-950">
                  {dataAppointment?.appointable.name}
                </div>
                {dataAppointment?.appointable.duration && (
                  <div className="flex flex-row items-center gap-1">
                    <FontAwesomeIcon
                      className="size-5 text-gray-500 dark:text-gray-400"
                      icon={faClock}
                    />
                    <div className="dark:text-gray-25 text-base font-semibold text-gray-950">
                      {Duration.fromObject({
                        minutes: dataAppointment.appointable.duration,
                      })
                        .rescale()
                        .toHuman({
                          unitDisplay: "narrow",
                          listStyle: "short",
                        })}
                    </div>
                  </div>
                )}
              </div>
              {dataAppointment?.appointable.description && (
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {dataAppointment.appointable.description}
                </div>
              )}
            </div>
            {dataAppointment?.status && (
              <BookingStatusAppointmentBadge appointment={dataAppointment} />
            )}
            <div className="flex flex-col gap-1.5">
              {dataAppointment?.appointmentHost && (
                <StaffAvatar
                  size="big"
                  userStaff={{
                    firstName: dataAppointment.appointmentHost.firstName,
                    lastName: dataAppointment.appointmentHost.lastName,
                    picture: dataAppointment.appointmentHost.picture,
                  }}
                />
              )}
              {dataAppointment?.startDate && (
                <div className="flex flex-row items-center gap-2">
                  <CalendarDateIcon
                    className="size-6"
                    pathClassName="stroke-gray-500 dark:stroke-gray-400"
                  />
                  <div className="dark:text-gray-25 line-clamp-1 text-base font-medium text-gray-950">
                    {DateTime.fromISO(dataAppointment.startDate)
                      .setZone(timezone)
                      .toLocaleString({
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                  </div>
                </div>
              )}
              {dataAppointment?.startDate && dataAppointment?.endDate && (
                <div className="flex flex-row items-center gap-2.5 pl-0.5">
                  <FontAwesomeIcon
                    className="size-5 text-gray-500 dark:text-gray-400"
                    icon={faClock}
                  />
                  <div className="dark:text-gray-25 line-clamp-1 text-sm font-semibold text-gray-950">
                    {`${DateTime.fromISO(dataAppointment.startDate)
                      .setZone(timezone)
                      .toLocaleString(
                        DateTime.TIME_SIMPLE,
                      )} - ${DateTime.fromISO(dataAppointment.endDate)
                      .setZone(timezone)
                      .toLocaleString(DateTime.TIME_SIMPLE)}`}
                  </div>
                </div>
              )}
              {dataAppointment?.appointmentFacility && (
                <FacilityAvatar
                  size="big"
                  showLocationIcon
                  facility={{
                    name: dataAppointment.appointmentFacility.name,
                    picture: dataAppointment.appointmentFacility.picture,
                  }}
                />
              )}
            </div>
            {isShowActions && (
              <div className="hidden flex-col gap-4 lg:flex">
                <HostedPagesProfileBookingAppointmentsOverviewActions
                  appointmentId={appointmentId}
                  dataAppointment={dataAppointment}
                />
              </div>
            )}
          </div>
        )}
      </div>
      {isShowActions && (
        <div className="flex justify-center px-4 lg:hidden">
          <div className="flex w-full max-w-lg flex-col gap-4">
            <HostedPagesProfileBookingAppointmentsOverviewActions
              appointmentId={appointmentId}
              dataAppointment={dataAppointment}
            />
          </div>
        </div>
      )}
    </div>
  );
};
